import React from 'react';
import { first } from 'lodash';

import { useBuildAccountUrl } from '@/lib/accounts/context';
import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Dropdown } from '@/components/dropdown';
import { useMetadataContext } from '@/explore/metadata-context';
import { Exploration } from '@/explore/types';
import { buildExplorationHashUrl } from '@/explore/utils';
import { buildExplorationByType, BuildType } from '@/core/exploration';

import styles from './add-button.module.scss';

interface AddDropdownProps {
  type: 'templates' | 'code';
  onClick: (exploration: Exploration) => void;
}

export const AddDropdown = ({ type, onClick }: AddDropdownProps) => {
  const buildAccountUrl = useBuildAccountUrl();
  const { models } = useMetadataContext();
  const baseModelId = first(models)?.modelId ?? '';
  const iconSize = 16;

  const handleClick = (event: React.MouseEvent, type: BuildType) => {
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    onClick(buildExplorationByType(type, baseModelId));
  };

  const templateMenuItems = [
    {
      icon: <Icon name="Funnel" size={iconSize} />,
      label: 'Funnel chart',
      type: 'link' as const,
      href: buildAccountUrl(buildExplorationHashUrl(buildExplorationByType('funnel', baseModelId))),
      onClick: (e: React.MouseEvent) => handleClick(e, 'funnel'),
    },
    {
      icon: <Icon name="Cohort" size={iconSize} />,
      label: 'Retention chart',
      type: 'link' as const,
      href: buildAccountUrl(buildExplorationHashUrl(buildExplorationByType('cohort', baseModelId))),
      onClick: (e: React.MouseEvent) => handleClick(e, 'cohort'),
    },
  ];

  const codeMenuItems = [
    {
      icon: <Icon name="Database" size={iconSize} />,
      label: 'SQL',
      type: 'link' as const,
      href: buildAccountUrl(buildExplorationHashUrl(buildExplorationByType('sql', baseModelId))),
      onClick: (e: React.MouseEvent) => handleClick(e, 'sql'),
    },
    {
      icon: <Icon name="Python" size={iconSize} />,
      label: 'Python',
      type: 'link' as const,
      href: buildAccountUrl(buildExplorationHashUrl(buildExplorationByType('python', baseModelId))),
      onClick: (e: React.MouseEvent) => handleClick(e, 'python'),
    },
  ];

  return (
    <Dropdown
      items={type === 'code' ? codeMenuItems : templateMenuItems}
      align="left"
      trigger={(isOpen, setIsOpen) => (
        <Button
          size="small"
          variant="secondary"
          icon={<Icon name={type === 'code' ? 'Code' : 'Box'} size={16} />}
          className={styles.tooltipTrigger}
          onClick={() => setIsOpen(!isOpen)}>
          {type === 'code' ? 'Code' : 'Templates'}
          <Icon name="ChevronDown" />
        </Button>
      )}
    />
  );
};
